import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { Decimal } from 'decimal.js';

import { Observable, Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { projectStatuses } from '../../../components/project/common/project.constants';
import {
	Constants,
	HEIGHT_RANGES,
	SnackBarHelperComponent,
	TEXT,
	UNIT_OPTIONS,
	ValidatorHelper,
} from '../../../helpers';
import {
	ApplicationMethod,
	BulkCharge,
	CoatingSystem,
	Division,
	EmbeddedBOQCoatProduct,
	GetBoqCoatingSystemValidation,
	GetBulkBoqCloneValidation,
	GetCoatingSystemProductValidation,
	GetCoatingSystemValidation,
	HeightRange,
	LabourRate,
	Product,
	Project,
	ProjectTime,
	ReferenceCode,
	TargetProject,
} from '../../../interfaces';
import { BrandModel, CoatingSystemModel, ColourModel, UserModel } from '../../../models';
import { CoatingSystemFormComponent } from '../../../pages/coating-system/coating-system-form/coating-system-form.component';
import { PaintInformationComponent } from '../../../pages/project/project-details/boq/paint-information/paint-information.component';
import { BrandService } from '../../../services/brand.service';
import { ChargeService } from '../../../services/charge.service';
import { DivisionService } from '../../../services/division.service';
import { ProductService } from '../../../services/product.service';
import { ProjectService } from '../../../services/project.service';
import { BulkBoqEditTextConst } from './common/bulk-boq-edit-dialog-constants';
import { RolePermissionsService } from '../../../services/role-permissions.service';
import { MeasuringToolPage } from '../../../pages/measure-tool/common/measuring-tool.models';
import { MeasuringToolLayoutDataService } from '../../../pages/measure-tool/common/components/measuring-tool-layout/common/measuring-tool-layout.data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MeasureSystemService } from '../../../services/measure-system.service';

@Component({
	selector: 'app-bulk-boq-edit-dialog',
	templateUrl: './bulk-boq-edit-dialog.component.html',
	styleUrls: ['./bulk-boq-edit-dialog.component.scss'],
})
export class BulkBoqEditDialogComponent implements OnInit {
	@ViewChild(CoatingSystemFormComponent, { static: true })
	public coatingSystemFormRef: CoatingSystemFormComponent;
	@ViewChild(PaintInformationComponent, { static: true })
	public paintInformationFormRef: PaintInformationComponent;

	@Input()
	public set selectedItems(boqIds: string[]) {
		if (boqIds) {
			this.setCommonCoatingSystem(boqIds);
		}
		this.resetBulkCharge();
	}

	@Input()
	public set currentUser(currentUser: UserModel) {
		if (currentUser) {
			this._currentUser = currentUser;
			this.getUserProjects(this.currentUser);
			this.projectService.getProjectFilterDropdowns(this.project.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(dropdownData => {
				this.dropdownData = dropdownData;
			});
		}
	}
	public get currentUser(): UserModel {
		return this._currentUser;
	}
	@Input()
	public set referenceCodeTypes(referenceCodeTypes: ReferenceCode) {
		if (referenceCodeTypes) {
			this._referenceCodeTypes = referenceCodeTypes;
			this.profiles = this.referenceCodeTypes['profile'];
			this.substrates = this.referenceCodeTypes['substrate'];
			this.labourRates = this.referenceCodeTypes['labour_type'];
			if (this.profiles) {
				this.filterProfile();
			}
		}
	}
	public get referenceCodeTypes(): ReferenceCode {
		return this._referenceCodeTypes;
	}

	@Input()
	public discountEditable: boolean;
	@Input()
	public isAdditionalCharge: boolean = false;
	@Input()
	public isBoq = false;
	@Input()
	public isCloning: boolean;
	@Input()
	public project: Project;
	@Input()
	public variationId: string;

	public addTagText = 'New Sub Location:';
	public applicationMethods: Array<ApplicationMethod> = Constants.APPLICATION_METHODS_ARRAY;
	public brands: Array<BrandModel> = [];
	public brandsObservable: Observable<BrandModel[]>;
	public boqCoatingSystemValidators;
	public bulkBoqEditTextConst: typeof BulkBoqEditTextConst = BulkBoqEditTextConst;
	public bulkBoqEditValidators;
	public bulkCharge: BulkCharge;
	public coatingProductsValidators;
	public coatingSystem: CoatingSystem;
	public colour: ColourModel;
	public colours: ColourModel[];
	public divisions: Observable<Division[]>;
	public dropdownData;
	public environments: Array<string> = Constants.ENVIRONMENTS;
	public heightRangeOptions: HeightRange[] = [];
	public filteredProfile: ReferenceCode[] = [];
	public getCommonCoatingSystem: Subscription;
	public isAdmin = false;
	public isM2 = false;
	public labourRates: ReferenceCode[];
	public measurements: Array<string> = Constants.MEASUREMENTS;
	public products: Product[];
	public productsObservable: Observable<Product[]>;
	public profiles: ReferenceCode[];
	public projectTime: ProjectTime;
	public rates: LabourRate[];
	public showBoq: boolean = undefined;
	public substrates: ReferenceCode[];
	public selectedTabId = "additionalCharge";
	public tabs = [
		{ id: 'boq', tooltip: "BOQ", label: 'BOQ',  },
		{ id: 'additionalCharge', tooltip: "Additional Charge", label:'Additional Charge', },
	];
	public targetProject: TargetProject;
	public textConstants: typeof TEXT = TEXT;
	public userProjects: Project[] = [];
	public userProjectsObservable: Observable<Project[]>;
	public measuringToolPagesObservable: Observable<MeasuringToolPage[]>;
	public measurementSystem: string = 'metric';
	public pages: MeasuringToolPage[];
	public filteredPages: MeasuringToolPage[];
	public newPage: MeasuringToolPage;
	public showPageInput: boolean = false;
	public unitTypeOptions: { label: string, value: string }[] = [
		{ value: 'M2', label: 'm<sup>2</sup>' },
		{ value: 'Lineal Metre', label: 'Lineal Metre' },
		{ value: 'Item', label: 'Item' },
	];

	private destroyRef = inject(DestroyRef);
	private _currentUser: UserModel;
	private _referenceCodeTypes: ReferenceCode;

	constructor(
		private brandService: BrandService,
		private chargeService: ChargeService,
		private divisionService: DivisionService,
		private projectService: ProjectService,
		private productService: ProductService,
		private snack: SnackBarHelperComponent,
		private rolePermissionsService: RolePermissionsService,
		private measuringToolLayoutDataService: MeasuringToolLayoutDataService,
		protected measureSystemService: MeasureSystemService,
	) {}

	public ngOnInit(): void {
		this.getMeasurementSystem();
		this.showBoq = this.isBoq && this.isAdditionalCharge;
		this.resetBulkCharge();

		this.productsObservable = this.productService.postList({ isActive: true }).pipe(tap((products: Product[]) => (this.products = products)));
		this.brandsObservable = this.brandService.postList({ isActive: true }).pipe(tap((brands: BrandModel[]) => (this.brands = brands)));
		this.divisions = this.divisionService.postList({ isActive: true });
	}

	public clearInvalidInput(validators, formControlName: string): void {
		validators[formControlName].setValue('');
	}

	public unitTypeChanged(): void {
		this.bulkCharge.bulkBoq.dimensions.profile = undefined;
		this.filterProfile();
		this.checkM2();
		this.updateUnitsM2();
	}

	/**
	 * Update the Dimension Units (m2) value based on the profile and Unit value
	 */
	public updateUnitsM2() {
		if (!this.bulkCharge.bulkBoq.useEstimatedValues && this.bulkCharge.bulkBoq.dimensions.profile) {
			if (!this.bulkCharge.bulkBoq.dimensions.units) {
				this.bulkCharge.bulkBoq.dimensions.units = 0;
			}

			if (this.bulkCharge.bulkBoq.dimensions.profile && this.bulkCharge.bulkBoq.dimensions.profile.code1 && this.bulkCharge.bulkBoq.dimensions.units) {
				const float = parseFloat(this.bulkCharge.bulkBoq.dimensions.profile.code1);
				if (!isNaN(float)) {
					this.bulkCharge.bulkBoq.areaDimensionsUnits = new Decimal(this.bulkCharge.bulkBoq.dimensions.units).times(float).toNumber();
				}
			} else {
				this.bulkCharge.bulkBoq.areaDimensionsUnits = 0;
			}
		} else {
			this.bulkCharge.bulkBoq.areaDimensionsUnits = this.bulkCharge.bulkBoq.useEstimatedValues
				? this.bulkCharge.bulkBoq.estimatedDimensions.units
				: this.bulkCharge.bulkBoq.dimensions.units;
		}
	}

	/**
	 *
	 */
	public checkM2() {
		if (this.bulkCharge.bulkBoq.dimensions.unitType && this.bulkCharge.bulkBoq.dimensions.unitType === 'M2') {
			this.isM2 = true;
			this.bulkCharge.bulkBoq.dimensions.profile = undefined;
		} else {
			this.isM2 = false;
		}
	}

	/**
	 *
	 */
	public filterProfile() {
		if (this.bulkCharge.bulkBoq?.dimensions.unitType && this.bulkCharge.bulkBoq?.dimensions.unitType !== 'M2') {
			this.filteredProfile = [];
			this.profiles.forEach(profile => {
				if (profile.code2 === this.bulkCharge.bulkBoq.dimensions.unitType) {
					this.filteredProfile.push(profile);
				}
			});
		}
	}

	/**
	 * Called when the selected colour has been changed. Sets the boqs colour to the new colour
	 * @param colour
	 */
	public onColourChanged(colour: ColourModel) {
		this.colour = colour;
		this.bulkCharge.bulkBoq.colour = colour ? colour.id : undefined;
	}

	public onCoatingSystemProductChange() {
		this.boqCoatingSystemValidators.Substrate.validators = [Validators.required];
	}

	public onDifficultyChange(value: number): void {
		this.bulkCharge.bulkBoq.difficulty = Math.min(100, Math.max(0, value));
	}

	public onSave() {
		if (this.coatingSystem.title || this.coatingSystem.coatProducts?.length || this.coatingSystem.product || this.coatingSystem.substrate) {
			const errors = this.checkCoatingSystemForErrors();
			if (errors) {
				this.snack.snackError(errors);
				return null;
			}

			// convert coating system to embedded coating system before sending to backend
			this.bulkCharge.bulkBoq.coatingSystem = {
				title: this.coatingSystem.title,
				coatProducts: this.coatingSystem.coatProducts,
				chargeOutRates: this.coatingSystem.chargeOutRates,
			};

			this.bulkCharge.bulkBoq.substrate = this.coatingSystem.substrate.id;
			this.bulkCharge.bulkBoq.product = this.coatingSystem.product.id;
		}

		if (this.isCloning) {
			// Only require the new page if we're cloning, we've changed sublocation and there's pages available.
			// (pages are paginated upon sublocation selection)
			if (this.filteredPages && this.filteredPages?.length > 0 && !this.newPage) {
				this.snack.snackError('You must select a Sub Location 1 and Page.');
				return null;
			}
			this.bulkCharge.bulkBoq.targetProjectId = this.project.id;
			this.bulkCharge.bulkAdditionalCharge.targetProjectId = this.project.id;
			this.bulkCharge.newPageId = this.newPage?.id ?? undefined;
		}
		// Additional charge and boq sublocation value is shared when bulk editing/cloning per client request
		this.bulkCharge.bulkAdditionalCharge.subLocation1 = this.bulkCharge.bulkBoq.subLocation1;
		const updateObj: any = JSON.parse(JSON.stringify(this.bulkCharge));
		// Remove null values when bulk editing
		this.removeFalseyValues(updateObj.bulkBoq);
		this.removeFalseyValues(updateObj.bulkAdditionalCharge);
		// Remove null values from dimensions and charge out rates objects
		this.removeFalseyValues(updateObj.bulkBoq.dimensions);
		this.removeFalseyValues(updateObj.bulkBoq.chargeOutAmounts);
		// If dimensions or charge out rate are not being updated, remove the key
		if (Object.keys(updateObj.bulkBoq.dimensions).length === 0) {
			delete updateObj.bulkBoq.dimensions;
		}
		if (this.bulkCharge.bulkBoq.dimensions && this.bulkCharge.bulkBoq.dimensions.profile && this.bulkCharge.bulkBoq.dimensions.profile.id) {
			updateObj.bulkBoq.dimensions.profile = this.bulkCharge.bulkBoq.dimensions.profile.id;
		}
		if (Object.keys(updateObj.bulkBoq.chargeOutAmounts).length === 0) {
			delete updateObj.bulkBoq.chargeOutAmounts;
		}

		if (this.bulkCharge.bulkBoq.difficulty <= 0) {
			updateObj.bulkBoq.difficulty = 0;
		}
		this.showPageInput = false;
		this.newPage = undefined;
		this.pages = [];
		this.filteredPages = [];
		return updateObj;
	}

	public setUnitTypes(): void {
		this.checkM2();
		this.updateUnitsM2();
	}

	private removeFalseyValues(obj) {
		for (const key in obj) {
			if (obj.hasOwnProperty(key) && !obj[key]) {
				delete obj[key];
			}
		}
	}

	private checkCoatingSystemForErrors(): string {
		this.boqCoatingSystemValidators = GetBoqCoatingSystemValidation();
		this.boqCoatingSystemValidators.Substrate.validators = [];
		this.coatingProductsValidators = [];

		let err: string = '';
		// add coatingSystem to bulkBoq only if Coating System has at least one coatProducts
		// then validate Coating System
		if (this.coatingSystem.coatProducts.length) {
			this.bulkCharge.bulkBoq.coatingSystem = this.coatingSystem;
			this.coatingSystem.coatProducts.map((value: EmbeddedBOQCoatProduct) => {
				const validators = new UntypedFormGroup(GetCoatingSystemProductValidation());
				validators.patchValue({
					ApplicationMethod: value.applicationMethod,
					Product: value.product,
					Percentage: value.percentage,
					SpreadRate: value.spreadRate,
					ApplicationRate: value.targetRate,
				});
				this.coatingProductsValidators.push(validators);
				return value;
			});
			this.coatingProductsValidators.map((e: UntypedFormGroup) => {
				err += ValidatorHelper.checkErrors(e.controls) || '';
				return e;
			});
		}
		const coatingSystemFormGroup = new UntypedFormGroup(this.boqCoatingSystemValidators);
		coatingSystemFormGroup.patchValue({
			Title: this.coatingSystem.title,
			Substrate: this.coatingSystem.substrate,
			Product: this.coatingSystem.product,
			CoatProducts: this.coatingSystem.coatProducts,
		});
		err += ValidatorHelper.checkErrors(coatingSystemFormGroup.controls) || '';

		return err;
	}

	private getUserProjects(currentUser: UserModel): void {
		this.userProjectsObservable = this.projectService.getUserProjects(currentUser.id).pipe(
			tap((projects: Project[]) => {
				// If not admin, only want to be able to clone to active/target projects
				if (this.rolePermissionsService.isAdmin) {
					this.userProjects = projects;
				} else {
					this.userProjects = projects.filter(project => {
						return project.status === projectStatuses.active.value || project.status === projectStatuses.target.value;
					});
				}
			})
		);
	}

	public resetBulkCharge(): void {
		this.bulkCharge = {
			bulkBoq: {
				coatingSystem: undefined,
				discount: undefined,
				division: undefined,
				environment: undefined,
				targetProjectId: undefined,
				useEstimatedValues: undefined,
				subLocation1: undefined,
				subLocation2: undefined,
				subLocation3: undefined,
				substrate: undefined,
				product: undefined,
				heightRange: undefined,
				difficulty: undefined,
				colour: undefined,
				dimensions: {
					unitType: undefined,
					profile: undefined,
					units: undefined,
				},
				estimatedDimensions: {
					unitType: undefined,
					profile: undefined,
					units: undefined,
				},
				chargeOutAmounts: {
					comment: undefined,
					internalComment: undefined,
					ratePerUnit: undefined,
				},
				areaDimensionsUnits: undefined,
			},
			bulkAdditionalCharge: {
				environment: undefined,
				description: undefined,
				discount: undefined,
				comment: undefined,
				subLocation1: '',
				targetProjectId: undefined,
			},
			newPageId: undefined,
		};
		this.newPage = undefined;

		// Reset validators
		this.bulkBoqEditValidators = GetBulkBoqCloneValidation(this.bulkCharge);
	}

	/**
	 * Check if all selected boqs have the same Coating System,
	 * if so populate this.coatingSystem with its values, else create a new empty coatingSystem.
	 */
	private setCommonCoatingSystem(boqIds): void {
		// we need this subscription for ngBusy
		if (boqIds && boqIds.length > 0) {
			this.getCommonCoatingSystem = this.chargeService
				.getCommonCoatingSystem(boqIds, this.project.id)
				.pipe(
					finalize(() => {
						this.getCommonCoatingSystem.unsubscribe();
						this.boqCoatingSystemValidators = GetBoqCoatingSystemValidation();
						this.coatingProductsValidators = GetCoatingSystemValidation();
					})
				)
				.subscribe((commonCoatingSystem: CoatingSystem) => {
					let bulkCoatingSystem: CoatingSystem;
					if (commonCoatingSystem && commonCoatingSystem.coatProducts) {
						bulkCoatingSystem = commonCoatingSystem;
						if (bulkCoatingSystem.product && bulkCoatingSystem.substrate) {
							bulkCoatingSystem.brand = bulkCoatingSystem.product.brand;
						}
					} else {
						bulkCoatingSystem = new CoatingSystemModel({
							brand: '',
							coatProducts: [],
							ignoreList: [],
							product: undefined,
							substrate: undefined,
							title: '',
						});
					}
					// make deep copy of coating system
					this.coatingSystem = JSON.parse(JSON.stringify(bulkCoatingSystem));
				});
		}
	}

	/**
	 * Initialize the observable that keeps the pages array updated
	 * @private
	 */
	private setMeasuringToolPages(): void {
		this.measuringToolPagesObservable = this.measuringToolLayoutDataService.
		getAllPages(this.project?.id, this.variationId, !!this.variationId).
			pipe(
				takeUntilDestroyed(this.destroyRef),
				tap((pages: MeasuringToolPage[]) => {
					if (pages) {
						this.pages = pages;
						this.filterMTPages();
					} else {
						this.pages = [];
					}
				})
			);
	}

	public filterMTPages() {
		if (!this.pages || this.pages.length === 0) {
			this.setMeasuringToolPages();
			return;
		}

		this.filteredPages = this.pages.filter(page => page.subLocation === this.bulkCharge.bulkBoq.subLocation1);
		this.newPage = this.filteredPages[0] ?? undefined;
		this.showPageInput = this.filteredPages.length > 0;
	}

	public getUnitTypeLabel(unitType: string): string {
		return this.unitTypeOptions.find(option => option.value === unitType)?.label ?? unitType;
	}

	private getMeasurementSystem() {
		this.measureSystemService.preferredSystem.pipe(
			takeUntilDestroyed(this.destroyRef))
			.subscribe((system) => {
				this.measurementSystem = system;
				this.unitTypeOptions = UNIT_OPTIONS[system];
				this.heightRangeOptions = HEIGHT_RANGES[system];
			})
	}

	public onTabChange(tabId: string) {
		tabId === 'boq' ? this.showBoq = true : this.showBoq = false;
		this.selectedTabId = tabId;
	}

}
